import { Button } from '@headlessui/react';
import React from 'react';
import { twJoin, twMerge } from 'tailwind-merge';
import { useCopyToClipboard, useIsMounted, useTimeout } from 'usehooks-ts';
import { CopyIcon } from '../../assets/icons';
import { Tooltip } from '../Tooltip/Tooltip';

interface ClipboardButtonProps {
  text: string;
  className?: string;
  buttonClassName?: string;
  iconSize?: number;
}

/** Button to copy text to the clipboard.
 * @dev The navigator.clipboard API only works when using HTTPS or on localhost.
 */
export const ClipboardButton: React.FC<ClipboardButtonProps> = ({
  text,
  className,
  buttonClassName,
  iconSize,
}) => {
  const [isCopied, setIsCopied] = React.useState(false);
  const [, copyToClipboard] = useCopyToClipboard();
  const isMounted = useIsMounted();

  useTimeout(() => setIsCopied(false), isCopied ? 2000 : null);

  const handleCopyClick = () => {
    copyToClipboard(text)
      .then(() => (isMounted() ? setIsCopied(true) : null))
      .catch(console.error);
  };

  return (
    <Tooltip
      className={twMerge('group', className)}
      content={isCopied ? 'Copied!' : 'Copy to clipboard'}
      open={isCopied}
    >
      <Button onClick={handleCopyClick} aria-label="Copy to clipboard" className={buttonClassName}>
        <CopyIcon
          style={iconSize ? { height: iconSize, width: iconSize } : undefined}
          className={twJoin('group-active:fill-white/20', isCopied && 'fill-white/20')}
        />
      </Button>
    </Tooltip>
  );
};
